import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  TrendingUp, Wallet, ShieldAlert, Trophy, ChevronLeft, ChevronRight,
  Home, Megaphone, Server, Monitor, GraduationCap, Users as UsersIcon,
  Receipt, Plus, Trash2, PiggyBank, ArrowDownRight, ArrowUpRight, Crown,
} from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";
import { ROLE_META, normalizeRole } from "@/components/RoleBadge";

const MONTHS_IT = ["Januar", "Februar", "März", "April", "Nie", "Juni",
                   "Juli", "August", "September", "Oktober", "November", "Dezember"];

function currentMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function shiftMonth(iso, delta) {
  const [y, m] = iso.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
function monthLabel(iso) {
  const [y, m] = iso.split("-").map(Number);
  return `${MONTHS_IT[m - 1]} ${y}`;
}

const COST_META = {
  affitto:    { label: "Affitto",    icon: Home,          color: "text-amber-700",  bg: "bg-amber-100" },
  marketing:  { label: "Marketing",  icon: Megaphone,     color: "text-pink-700",   bg: "bg-pink-100" },
  crm:        { label: "CRM",        icon: Server,        color: "text-prime",      bg: "bg-prime/10" },
  software:   { label: "Software",   icon: Monitor,       color: "text-sky-700",    bg: "bg-sky-100" },
  formazione: { label: "Formazione", icon: GraduationCap, color: "text-violet-700", bg: "bg-violet-100" },
  stipendi:   { label: "Stipendi",   icon: UsersIcon,     color: "text-indigo-700", bg: "bg-indigo-100" },
  altro:      { label: "Andere",      icon: Receipt,       color: "text-slate-700",  bg: "bg-slate-100" },
};

export default function CEOPage() {
  const [month, setMonth] = useState(currentMonthISO());
  const [data, setData] = useState(null);
  const [costs, setCosts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newCost, setNewCost] = useState({ category: "altro", label: "", monthly_amount: 0 });

  const loadAll = async () => {
    setBusy(true);
    try {
      const [d, c] = await Promise.all([
        api.get(`/ceo/dashboard?month=${month}`),
        api.get("/ceo/costs"),
      ]);
      setData(d.data);
      setCosts(c.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [month]);

  const addCost = async () => {
    if (!newCost.label || !newCost.monthly_amount) {
      toast.error("Eingeben nome e importo");
      return;
    }
    try {
      await api.post("/ceo/costs", newCost);
      setNewCost({ category: "altro", label: "", monthly_amount: 0 });
      toast.success("Kosten hinzugefügt");
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const patchCost = async (id, patch) => {
    try {
      await api.patch(`/ceo/costs/${id}`, patch);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const removeCost = async (id) => {
    if (!window.confirm("Löschen questa voce di costo?")) return;
    try {
      await api.delete(`/ceo/costs/${id}`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const totalCosts = useMemo(() => costs.filter(c => c.active !== false)
    .reduce((s, c) => s + (Number(c.monthly_amount) || 0), 0), [costs]);

  const profitClass = (data?.profit || 0) >= 0 ? "text-green-700" : "text-red-700";
  const profitIcon = (data?.profit || 0) >= 0 ? ArrowUpRight : ArrowDownRight;

  return (
    <div className="space-y-6" data-testid="ceo-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-uppercase text-prime">/ Dashboard CEO · Master</div>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight mt-1">
            Direktion & Performance
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Produktion, classifica consulenti, costi operativi e Profit & Loss.
          </p>
        </div>
        <div className="flex items-center gap-2 prime-card px-2 py-1.5" data-testid="month-switcher">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="month-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-sm font-display font-bold tracking-tight min-w-[140px] text-center">
            {monthLabel(month)}
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMonth(shiftMonth(month, +1))} data-testid="month-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Big KPIs (CEO numbers) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <CeoCard icon={TrendingUp} label="Gesamtproduktion"
                 value={data ? `CHF ${fmtMoney(data.production_total)}` : "—"}
                 hint={data ? `YTD: CHF ${fmtMoney(data.production_ytd)}` : ""}
                 accent="border-l-4 border-foreground" color="text-foreground"
                 testId="kpi-prod" />
        <CeoCard icon={Wallet} label="Generierte Provisionen"
                 value={data ? `CHF ${fmtMoney(data.commissions_total)}` : "—"}
                 hint={data ? `Quota azienda: CHF ${fmtMoney(data.company_share)}` : ""}
                 accent="border-l-4 border-prime" color="text-prime"
                 testId="kpi-comm" />
        <CeoCard icon={ShieldAlert} label="Storno-Reserve"
                 value={data ? `CHF ${fmtMoney(data.storno_fund)}` : "—"}
                 hint="10% zurückbehalten, freigegeben nach 3 Monaten"
                 accent="border-l-4 border-amber-500" color="text-amber-700"
                 testId="kpi-storno" />
        <CeoCard icon={profitIcon} label="Utile azienda"
                 value={data ? `CHF ${fmtMoney(data.profit)}` : "—"}
                 hint={data ? `Quota azienda − costi (${monthLabel(month)})` : ""}
                 accent={`border-l-4 ${(data?.profit || 0) >= 0 ? "border-green-500" : "border-red-500"}`}
                 color={profitClass} testId="kpi-profit" />
      </div>

      {/* Two-column: Leaderboard + Costs */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Leaderboard */}
        <div className="prime-card xl:col-span-2">
          <div className="px-6 py-4 border-b border-border flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" />
            <h2 className="font-display font-bold text-xl">Classifica consulenti · {monthLabel(month)}</h2>
          </div>
          {!data || data.leaderboard.length === 0 ? (
            <div className="p-12 text-center">
              <Trophy className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">Keina produzione registrata in questo periodo.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leaderboard-table">
                <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr className="text-left">
                    <th className="px-4 py-2 font-medium w-12">#</th>
                    <th className="px-4 py-2 font-medium">Consulente</th>
                    <th className="px-4 py-2 font-medium">Rolle</th>
                    <th className="px-4 py-2 font-medium text-right">Policen</th>
                    <th className="px-4 py-2 font-medium text-right">Produktion</th>
                    <th className="px-4 py-2 font-medium text-right">Commissioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.leaderboard.map((e, idx) => {
                    const userRole = normalizeRole(e.role);
                    const meta = ROLE_META[userRole] || ROLE_META.basic;
                    const medal = idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : null;
                    return (
                      <tr key={e.user_id} className="hover:bg-secondary/30" data-testid={`leaderboard-row-${idx}`}>
                        <td className="px-4 py-2 font-display font-extrabold text-lg text-foreground/80">
                          {medal || `#${idx + 1}`}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <div className={`w-7 h-7 rounded-full ${meta.bg} text-white flex items-center justify-center text-xs font-bold`}>
                              {(e.full_name || "?").slice(0, 1).toUpperCase()}
                            </div>
                            <span className="font-medium">{e.full_name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${meta.bgSoft} ${meta.text}`}>
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right font-mono">{e.policies}</td>
                        <td className="px-4 py-2 text-right font-mono font-bold">CHF {fmtMoney(e.production)}</td>
                        <td className="px-4 py-2 text-right font-mono text-prime">CHF {fmtMoney(e.commissions)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* P&L Summary */}
        <div className="prime-card">
          <div className="px-6 py-4 border-b border-border flex items-center gap-2">
            <PiggyBank className="w-4 h-4 text-prime" />
            <h2 className="font-display font-bold text-xl">Profit &amp; Loss</h2>
          </div>
          <div className="p-5 space-y-3 text-sm">
            <PLRow label="Entrate (premi)" value={data?.production_total || 0} />
            <PLRow label="Beraterprovisionen" value={-(data?.consultant_payouts || 0)} negative />
            <PLRow label="Quota azienda" value={data?.company_share || 0} accent="text-prime" bold />
            <hr className="border-border" />
            <PLRow label="Betriebskosten" value={-totalCosts} negative />
            <hr className="border-border" />
            <div className="flex items-center justify-between pt-2">
              <span className="font-display font-bold text-base">Utile netto</span>
              <span className={`font-display font-extrabold text-2xl ${profitClass}`}>
                {data ? `CHF ${fmtMoney(data.profit)}` : "—"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Costs management */}
      <div className="prime-card">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-prime" />
            <h2 className="font-display font-bold text-xl">Costi azienda ({costs.length})</h2>
          </div>
          <div className="text-sm">
            <span className="text-muted-foreground">Monatsgesamt:</span>{" "}
            <span className="font-display font-extrabold text-prime">CHF {fmtMoney(totalCosts)}</span>
          </div>
        </div>

        {/* Add new cost */}
        <div className="px-6 py-3 bg-secondary/30 border-b border-border flex items-end gap-2 flex-wrap" data-testid="add-cost-row">
          <div>
            <div className="label-uppercase text-muted-foreground mb-1">Kategorie</div>
            <Select value={newCost.category} onValueChange={v => setNewCost({ ...newCost, category: v })}>
              <SelectTrigger className="h-9 w-36" data-testid="new-cost-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(COST_META).map(([key, m]) => (
                  <SelectItem key={key} value={key}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <div className="label-uppercase text-muted-foreground mb-1">Beschreibung</div>
            <Input value={newCost.label} onChange={e => setNewCost({ ...newCost, label: e.target.value })}
                   placeholder="Es. Ufficio Lugano" className="h-9" data-testid="new-cost-label" />
          </div>
          <div>
            <div className="label-uppercase text-muted-foreground mb-1">CHF / Monat</div>
            <Input type="number" value={newCost.monthly_amount}
                   onChange={e => setNewCost({ ...newCost, monthly_amount: Number(e.target.value) })}
                   className="h-9 w-32 font-mono" data-testid="new-cost-amount" />
          </div>
          <Button onClick={addCost} className="btn-prime gap-1.5 h-9" data-testid="add-cost-btn">
            <Plus className="w-4 h-4" /> Hinzufügen
          </Button>
        </div>

        <div className="divide-y divide-border">
          {costs.map(c => {
            const m = COST_META[c.category] || COST_META.altro;
            const Icon = m.icon;
            return (
              <div key={c.id} className="px-6 py-3 flex items-center gap-3 flex-wrap" data-testid={`cost-row-${c.id}`}>
                <div className={`w-9 h-9 rounded-md ${m.bg} ${m.color} flex items-center justify-center shrink-0`}>
                  <Icon className="w-4 h-4" strokeWidth={1.8} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-[10px] uppercase tracking-wider font-bold ${m.color}`}>{m.label}</div>
                  <Input
                    defaultValue={c.label} onBlur={e => e.target.value !== c.label && patchCost(c.id, { label: e.target.value })}
                    className="h-7 px-2 text-sm" data-testid={`cost-label-${c.id}`}
                  />
                </div>
                <Input type="number" defaultValue={c.monthly_amount}
                       onBlur={e => Number(e.target.value) !== c.monthly_amount && patchCost(c.id, { monthly_amount: Number(e.target.value) })}
                       className="h-7 w-28 font-mono text-right" data-testid={`cost-amount-${c.id}`} />
                <button
                  onClick={() => patchCost(c.id, { active: !c.active })}
                  className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded font-bold ${c.active === false ? "bg-slate-200 text-slate-600" : "bg-green-100 text-green-700"}`}
                  data-testid={`cost-active-${c.id}`}
                >
                  {c.active === false ? "Inattivo" : "Aktiv"}
                </button>
                <button onClick={() => removeCost(c.id)} className="text-muted-foreground hover:text-destructive" data-testid={`cost-delete-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Costs breakdown chart-like */}
      {data && data.costs_breakdown.length > 0 && (
        <div className="prime-card p-6">
          <div className="label-uppercase text-muted-foreground mb-3">Ripartizione costi</div>
          <div className="space-y-2">
            {data.costs_breakdown.map(b => {
              const m = COST_META[b.category] || COST_META.altro;
              const pct = totalCosts > 0 ? (b.monthly_amount / totalCosts) * 100 : 0;
              return (
                <div key={b.category} data-testid={`breakdown-${b.category}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className={`font-bold uppercase tracking-wider ${m.color}`}>{m.label}</span>
                    <span className="font-mono">CHF {fmtMoney(b.monthly_amount)} · {pct.toFixed(1)}%</span>
                  </div>
                  <div className="h-2 bg-secondary rounded overflow-hidden">
                    <div className={`h-full ${m.bg.replace("100", "500").replace("bg-prime/10", "bg-prime")}`}
                         style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function CeoCard({ icon: Icon, label, value, hint, color = "text-foreground", accent = "", testId }) {
  return (
    <div className={`prime-card p-5 ${accent}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="label-uppercase text-muted-foreground">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />}
      </div>
      <div className={`font-display font-extrabold text-2xl sm:text-3xl tracking-tight ${color}`}>
        {value}
      </div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function PLRow({ label, value, negative = false, accent = "text-foreground", bold = false }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-mono ${negative ? "text-red-700" : accent} ${bold ? "font-bold" : ""}`}>
        {negative ? "−" : ""}CHF {fmtMoney(Math.abs(value || 0))}
      </span>
    </div>
  );
}
